import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { QUESTIONS, scoreQuiz } from "../data/peel-quiz";
import { useBookingLink } from "../lib/useBookingLinks";
import { trackSchedule } from "../lib/tracking";
import { PHONE_TEL } from "../data/site";

const Progress = ({ step }) => (
  <div className="flex gap-1.5" data-testid="quiz-progress">
    {QUESTIONS.map((q, i) => <span key={q.q} className={`h-0.5 flex-1 transition-colors ${i <= step ? "bg-neutral-950" : "bg-neutral-300"}`} />)}
  </div>
);

const Question = ({ step, onPick, onBack }) => {
  const { q, options } = QUESTIONS[step];
  return (
    <div key={step} className="rise" data-testid={`quiz-question-${step}`}>
      <p className="kicker mt-8">Question {step + 1} of {QUESTIONS.length}</p>
      <h2 className="font-display mt-3 text-3xl font-light leading-tight text-neutral-950 sm:text-4xl">{q}</h2>
      <div className="mt-8 grid gap-2">
        {options.map((o, i) => (
          <button key={o.label} type="button" onClick={() => onPick(i)} data-testid={`quiz-option-${step}-${i}`}
            className="min-h-[56px] border border-neutral-300 px-5 py-3 text-left text-base font-light text-neutral-800 transition-colors hover:border-neutral-950 hover:bg-neutral-950 hover:text-white">
            {o.label}
          </button>
        ))}
      </div>
      {step > 0 && (
        <button type="button" onClick={onBack} className="mt-6 inline-flex items-center gap-2 text-sm font-light text-neutral-600 hover:text-neutral-950" data-testid="quiz-back-btn">
          <ArrowLeft size={14} strokeWidth={1.5} /> Back
        </button>
      )}
    </div>
  );
};

const Result = ({ answers, onRestart }) => {
  const r = scoreQuiz(answers);
  const book = useBookingLink(r.service);
  return (
    <div className="rise mt-8" data-testid="quiz-result">
      <p className="kicker">Your match</p>
      <h2 className="font-display mt-3 text-4xl font-light leading-tight text-neutral-950 sm:text-5xl" data-testid="quiz-result-name">{r.name}</h2>
      <p className="mt-4 text-xs uppercase tracking-[0.25em] text-neutral-950" data-testid="quiz-result-price">{r.price}</p>
      <p className="mt-6 text-base font-light leading-relaxed text-neutral-600">{r.why}</p>
      <ul className="mt-6 space-y-2">
        {r.expect.map((e) => <li key={e} className="flex items-start gap-3 text-sm font-light text-neutral-800"><Check size={16} strokeWidth={1.5} className="mt-0.5 shrink-0" />{e}</li>)}
      </ul>
      <div className="mt-10 flex flex-col gap-3 sm:flex-row">
        <a href={book} target="_blank" rel="noreferrer" onClick={trackSchedule} className="btn-ink" data-testid="quiz-book-btn">Book {r.name}</a>
        <Link to={r.page} className="btn-line" data-testid="quiz-learn-link">Learn More</Link>
      </div>
      <p className="mt-6 text-sm font-light text-neutral-600">Your result is a starting point. We confirm the right peel for your skin at your visit. Questions? <a href={`sms:${PHONE_TEL}`} className="underline" data-testid="quiz-text-link">Text us</a>.</p>
      <button type="button" onClick={onRestart} className="link-under mt-8" data-testid="quiz-restart-btn">Retake the Quiz</button>
    </div>
  );
};

export default function PeelQuiz() {
  const [answers, setAnswers] = useState([]);
  const step = answers.length;
  const done = step === QUESTIONS.length;
  return (
    <main className="bg-[hsl(var(--stone))] py-16 md:py-24" data-testid="peel-quiz-page">
      <div className="mx-auto max-w-[680px] px-5">
        <p className="kicker">VI Peel® vs Cosmelan® · Scottsdale</p>
        <h1 className="font-display mt-4 text-4xl font-light leading-[1.05] text-neutral-950 sm:text-5xl lg:text-6xl" data-testid="quiz-title">Which peel is <em>right for me?</em></h1>
        <p className="mt-5 max-w-md text-base font-light text-neutral-600">Five quick questions. Your match appears instantly, and you can book it right away.</p>
        <div className="mt-10 border border-neutral-200 bg-white p-6 sm:p-10">
          <Progress step={done ? step : step - 1} />
          {done
            ? <Result answers={answers} onRestart={() => setAnswers([])} />
            : <Question step={step} onPick={(i) => setAnswers([...answers, i])} onBack={() => setAnswers(answers.slice(0, -1))} />}
        </div>
        <Compare />
      </div>
    </main>
  );
}

const ROWS = [
  ["Best for", "Acne, aging, tone, texture, mild pigment", "Melasma and stubborn dark spots"],
  ["Price", "$349", "$1,000"],
  ["Downtime", "Light peeling, about day 3 to 7", "Redness and peeling, about 5 to 7 days"],
  ["Home care", "One-week post-peel kit", "Guided routine for several months"],
  ["Results", "Brighter skin in about a week", "Visible in 2 to 4 weeks, full at 3 months"],
];

const Compare = () => (
  <section className="mt-16" data-testid="peel-compare">
    <h2 className="font-display text-3xl font-light text-neutral-950">At a glance</h2>
    <div className="mt-6 overflow-x-auto">
      <table className="w-full min-w-[480px] border-collapse text-left text-sm font-light">
        <thead><tr className="border-b border-neutral-950 text-[11px] uppercase tracking-[0.2em] text-neutral-700"><th className="py-3 pr-4" /><th className="py-3 pr-4">VI Peel®</th><th className="py-3">Cosmelan®</th></tr></thead>
        <tbody>{ROWS.map(([k, a, b]) => <tr key={k} className="border-b border-neutral-300"><td className="py-4 pr-4 text-neutral-950">{k}</td><td className="py-4 pr-4 text-neutral-700">{a}</td><td className="py-4 text-neutral-700">{b}</td></tr>)}</tbody>
      </table>
    </div>
  </section>
);
